import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { KeyRound, ArrowLeft, Check, ShieldAlert } from "lucide-react";

/**
 * Consumes the `password_setup_token` embedded in invite / welcome emails.
 * Route: /set-password?token=<token>
 *
 * Flow:
 *  1. On mount, call /auth/verify-setup-token to check the token is valid.
 *  2. If valid, show the "choose your password" form (greet by name).
 *  3. On submit, POST /auth/set-password; on success, redirect to /login.
 *  4. If the token is invalid/expired, show a helpful state pointing the user
 *     to the forgot-password flow.
 */
export default function SetPassword() {
  const { branding } = useTheme();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [verifyState, setVerifyState] = useState({ status: "checking", email: null, name: null, message: null });

  useEffect(() => {
    if (!token) { setVerifyState({ status: "no-token" }); return; }
    let alive = true;
    api.get(`/auth/verify-setup-token`, { params: { token } })
      .then((r) => { if (alive) setVerifyState({ status: "ok", email: r.data?.email, name: r.data?.name }); })
      .catch((e) => { if (alive) setVerifyState({ status: "invalid", message: apiErrorMessage(e, "This setup link is not valid.") }); });
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    if (pw.length < 8) { toast.error("Password must be 8+ characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/set-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not set password"));
    } finally { setBusy(false); }
  };

  // ── Renderers ─────────────────────────────────────────────────────────
  if (verifyState.status === "no-token" || verifyState.status === "invalid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6">
        <div className="max-w-md border border-border p-10 text-center" data-testid="set-password-invalid">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-6">
            <ShieldAlert className="w-6 h-6 text-amber-600" />
          </div>
          <h1 className="heading-display text-2xl mb-4">This link isn&apos;t valid.</h1>
          <p className="text-sm text-foreground/70 mb-6">
            {verifyState.message || "The setup link is missing or already used. Ask your admin to send a new one, or reset your password."}
          </p>
          <div className="flex gap-2 justify-center">
            <Link to="/forgot-password"><Button className="rounded-none" data-testid="set-password-forgot-btn">Reset password</Button></Link>
            <Link to="/login"><Button variant="outline" className="rounded-none" data-testid="set-password-login-btn">Back to login</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  if (verifyState.status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="set-password-checking">
        <div className="text-sm text-foreground/60">Verifying your link…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-16">
      <div className="max-w-lg w-full" data-testid="set-password-page">
        <Link to="/login" className="inline-flex items-center gap-2 text-xs text-foreground/60 hover:text-foreground mb-8" data-testid="set-password-back-login">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to log in
        </Link>
        {done ? (
          <div className="border border-border p-10" data-testid="set-password-done">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-6">
              <Check className="w-6 h-6 text-primary" />
            </div>
            <h1 className="heading-display text-3xl mb-3">You&apos;re all set.</h1>
            <p className="text-sm text-foreground/80">Redirecting you to log in…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-border p-10">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-5">
              <KeyRound className="w-5 h-5 text-primary" />
            </div>
            <div className="label-eyebrow mb-2">{branding.platform_name}</div>
            <h1 className="heading-display text-3xl mb-2">
              {verifyState.name ? `Welcome, ${verifyState.name.split(" ")[0]}.` : "Set your password."}
            </h1>
            <p className="text-sm text-foreground/70 mb-6">
              Choose a strong password to activate your account
              {verifyState.email ? <> — <span className="font-mono text-xs">{verifyState.email}</span></> : "."}
            </p>
            <div className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">New password</Label>
                <Input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className="h-11" data-testid="set-password-pw" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest font-mono mb-2 inline-block">Confirm password</Label>
                <Input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} className="h-11" data-testid="set-password-pw2" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 rounded-none font-bold uppercase tracking-widest text-xs" data-testid="set-password-submit">
                {busy ? "Saving…" : "Activate account"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
